import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Shield, Plus, Edit2, Trash2, Check,
    Building2, User, AlertCircle,
    CheckCircle, XCircle, RefreshCw, ChevronDown, ChevronRight, Users
} from 'lucide-react';
import { kycService } from '../../services/kycService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../context/AuthContext';

const BO_TYPES = ['BO_DECLARATION', 'BO_REGISTER', 'OWNERSHIP_CHAIN', 'SOURCE_OF_WEALTH', 'SOURCE_OF_FUNDS', 'CR12_FORM'];

const KycSettings = () => {
    const [kycRequirements, setKycRequirements] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [collapsedSections, setCollapsedSections] = useState({});
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    const canCreateRequirement = hasPermission('KYC_CONFIG:CREATE');
    const canEditRequirement = hasPermission('KYC_CONFIG:UPDATE');
    const canDeleteRequirement = hasPermission('KYC_CONFIG:DELETE');

    useEffect(() => {
        fetchCompanies();
    }, []);

    useEffect(() => {
        fetchKycRequirements();
    }, [selectedCompanyId]);

    const fetchCompanies = async () => {
        try {
            const response = await companyService.getAll();
            const data = (response.success && response.data) ? response.data : (Array.isArray(response) ? response : []);
            setCompanies(data);
            if (data.length > 0 && selectedCompanyId === null) {
                setSelectedCompanyId(data[0].id);
            }
        } catch (error) {
            console.error('Failed to fetch companies:', error);
        }
    };

    const fetchKycRequirements = async () => {
        try {
            setLoading(true);
            let response;
            if (selectedCompanyId) {
                response = await kycService.kycRequirements.getByCompany(selectedCompanyId);
            } else {
                response = await kycService.kycRequirements.getAll();
            }
            const requirements = (response.success && response.data) ? response.data : (Array.isArray(response) ? response : []);
            setKycRequirements(Array.isArray(requirements) ? requirements : []);
        } catch (error) {
            console.error('Failed to fetch KYC requirements:', error);
            showMessage('Failed to fetch KYC requirements', 'error');
            setKycRequirements([]);
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    const handleDeleteRequirement = async (id) => {
        if (window.confirm('Are you sure you want to delete this KYC requirement?')) {
            try {
                await kycService.kycRequirements.delete(id);
                showMessage('KYC requirement deleted successfully');
                fetchKycRequirements();
            } catch (error) {
                console.error('Failed to delete KYC requirement:', error);
                showMessage('Failed to delete KYC requirement', 'error');
            }
        }
    };

    const handleAddRequirement = () => {
        navigate('/compliance/kyc/settings/new');
    };

    const handleEditRequirement = (id) => {
        navigate(`/compliance/kyc/settings/${id}/edit`);
    };

    const formatDocumentType = (type) => {
        if (!type) return 'N/A';
        return type.replace(/_/g, ' ').toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const toggleSection = (key) => {
        setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const isBORequirement = (req) => {
        return BO_TYPES.includes(req.type) || (req.name && req.name.startsWith('BO '));
    };

    // Group requirements into 3 sections
    const retailReqs = kycRequirements.filter(r => r.applicableClientType === 'RETAIL');
    const corporateStandardReqs = kycRequirements.filter(r => r.applicableClientType === 'CORPORATE' && !isBORequirement(r));
    const boReqs = kycRequirements.filter(r => r.applicableClientType === 'CORPORATE' && isBORequirement(r));

    const sections = [
        { key: 'retail', label: 'Retail Requirements', icon: User, reqs: retailReqs, color: 'blue' },
        { key: 'corporate', label: 'Corporate Requirements', icon: Building2, reqs: corporateStandardReqs, color: 'purple' },
        { key: 'bo', label: 'Beneficial Ownership', icon: Users, reqs: boReqs, color: 'amber', subtitle: 'Corporate only' },
    ];

    const RequirementTable = ({ requirements }) => (
        <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requirement</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document Type</th>
                    {!selectedCompanyId && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mandatory</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
                {requirements.map((req) => (
                    <tr key={req.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                                <p className="text-sm font-medium text-gray-900">{req.name}</p>
                                {req.description && (
                                    <p className="text-xs text-gray-500 truncate max-w-xs">{req.description}</p>
                                )}
                            </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs rounded-full border bg-gray-50 text-gray-700 border-gray-300">
                                {formatDocumentType(req.type)}
                            </span>
                        </td>
                        {!selectedCompanyId && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {req.companyName || req.company?.name || 'N/A'}
                            </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                            {req.mandatory ? (
                                <span className="flex items-center gap-1 text-green-600">
                                    <CheckCircle className="w-4 h-4" />
                                    <span className="text-sm">Yes</span>
                                </span>
                            ) : (
                                <span className="flex items-center gap-1 text-gray-400">
                                    <XCircle className="w-4 h-4" />
                                    <span className="text-sm">No</span>
                                </span>
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                                {canEditRequirement && (
                                    <button onClick={() => handleEditRequirement(req.id)} className="text-gray-600 hover:text-indigo-600 transition" title="Edit">
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                )}
                                {canDeleteRequirement && (
                                    <button onClick={() => handleDeleteRequirement(req.id)} className="text-gray-600 hover:text-red-600 transition" title="Delete">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <div className="space-y-6">
            {message.text && (
                <div className={`p-4 rounded-lg flex items-center gap-2 ${
                    message.type === 'error'
                        ? 'bg-red-100 text-red-800 border border-red-200'
                        : 'bg-green-100 text-green-800 border border-green-200'
                }`}>
                    {message.type === 'error' ? <AlertCircle /> : <Check />}
                    {message.text}
                </div>
            )}

            {/* Header */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center">
                            <Shield className="w-6 h-6 text-gray-700" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">KYC Settings</h2>
                            <p className="text-sm text-gray-500">Manage KYC requirements for document collection</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={fetchKycRequirements}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition"
                        >
                            <RefreshCw className="w-5 h-5" />
                        </button>
                        {canCreateRequirement && (
                            <button
                                onClick={handleAddRequirement}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                            >
                                <Plus className="w-5 h-5" />
                                Add Requirement
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Company Tabs */}
            <div className="bg-white rounded-xl border border-gray-200">
                <div className="border-b border-gray-200 px-4 pt-3">
                    <div className="flex items-center gap-1 overflow-x-auto pb-0">
                        {companies.map(company => (
                            <button
                                key={company.id}
                                onClick={() => setSelectedCompanyId(company.id)}
                                className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg border-b-2 transition ${
                                    selectedCompanyId === company.id
                                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                <span className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4" />
                                    {company.name}
                                </span>
                            </button>
                        ))}
                        <button
                            onClick={() => setSelectedCompanyId(null)}
                            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg border-b-2 transition ${
                                selectedCompanyId === null
                                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            All Companies ({kycRequirements.length})
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
                        </div>
                    ) : kycRequirements.length === 0 ? (
                        <div className="text-center py-12">
                            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">No KYC requirements found</p>
                            {canCreateRequirement && (
                                <button onClick={handleAddRequirement} className="mt-4 text-blue-600 hover:text-blue-700 font-medium">
                                    Add your first requirement
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {sections.map(({ key, label, icon: Icon, reqs, color, subtitle }) => {
                                if (reqs.length === 0) return null;
                                const isCollapsed = collapsedSections[key];
                                return (
                                    <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                                        <button
                                            onClick={() => toggleSection(key)}
                                            className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Icon className={`w-5 h-5 text-${color}-600`} />
                                                <span className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                                                    {label}
                                                </span>
                                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${color}-100 text-${color}-700`}>
                                                    {reqs.length}
                                                </span>
                                                {subtitle && (
                                                    <span className="text-xs text-gray-500 italic">
                                                        — {subtitle}
                                                    </span>
                                                )}
                                            </div>
                                            {isCollapsed ? (
                                                <ChevronRight className="w-5 h-5 text-gray-400" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-gray-400" />
                                            )}
                                        </button>
                                        {!isCollapsed && (
                                            <RequirementTable requirements={reqs} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KycSettings;
